import { createContext, createElement, ReactNode, RefObject, useContext } from 'react';
import type { ChatComposerHandle } from './components/ChatComposer';

export interface AiChatOptionPickHandlers {
    /** Sends the clicked choice's text as an ordinary new user turn. The part composes it; this sends it. */
    onSelect: (text: string) => void;
    /**
     * True while a turn is streaming, when `onSelect` can do nothing: `send` refuses. The choices are
     * drawn disabled to match — a control that looks live and does nothing is a lie.
     */
    isDisabled: boolean;
    /** Asks for the last answer again. Absent where the conversation offers no retry, which hides it. */
    retry?: () => void;
    /** Says `text` through the conversation's one status region, which the composer mounts. */
    announce: (text: string) => void;
    /** Puts the cursor in the composer. */
    focus: () => void;
    /** Replaces the composer's text, ending any template being filled. */
    setDraft: (text: string) => void;
    /** Shows `text` as the composer's placeholder; null gives the surface its own back. */
    setPlaceholder: (text: string | null) => void;
}

/**
 * How a message part reaches the surface that owns the conversation: the surface's send, its retry, and its
 * composer. Each surface has its own composer and its own send, so the message parts read them from here
 * rather than being handed them through every renderer between. Defaults to no-ops so a message renders fine
 * outside any surface.
 */
const NO_SURFACE: AiChatOptionPickHandlers = {
    onSelect: () => {},
    isDisabled: false,
    announce: () => {},
    focus: () => {},
    setDraft: () => {},
    setPlaceholder: () => {},
};

const AiChatOptionPickContext = createContext<AiChatOptionPickHandlers>(NO_SURFACE);

type ComposerAction = 'announce' | 'focus' | 'setDraft' | 'setPlaceholder';

/** A provider may leave the composer's actions out, as one with no composer does; they stay no-ops. */
export function AiChatOptionPickProvider({
    value,
    children,
}: {
    value: Omit<AiChatOptionPickHandlers, ComposerAction> & Partial<Pick<AiChatOptionPickHandlers, ComposerAction>>;
    children: ReactNode;
}) {
    return createElement(AiChatOptionPickContext.Provider, { value: { ...NO_SURFACE, ...value } }, children);
}

export function useAiChatOptionPick() {
    return useContext(AiChatOptionPickContext);
}

/** A surface's composer actions for its context, read off the composer's handle at call time. */
export const composerActions = (
    composer: RefObject<ChatComposerHandle | null>
): Pick<AiChatOptionPickHandlers, ComposerAction> => ({
    announce: (text) => composer.current?.announce(text),
    focus: () => composer.current?.focus(),
    setDraft: (text) => composer.current?.setDraft(text),
    setPlaceholder: (text) => composer.current?.setPlaceholder(text),
});
